import { Component, computed, ChangeDetectionStrategy } from '@angular/core';

import { BulkModalService, BulkModalType } from '../../services/bulk-modal.service';
import { BulkTileCreationUploadComponent } from '../../../creation/components/bulk-tile-creation-upload/bulk-tile-creation-upload.component';
import { BulkProcessingComponent } from '../bulk-processing/bulk-processing.component';
import { BulkTileConfigurationUploadComponent } from '../../../configuration/components/bulk-tile-configuration-upload/bulk-tile-configuration-upload.component';
import { TileProcessingItem } from '../../models/csv-tile';

@Component({
  selector: 'motn-bulk-modal',
  template: `
    @let state = modalState();
    @if (state.type !== BulkModalType.None) {
      <div class="modal-backdrop" (click)="onBackdropClick($event)">
        <div class="modal-container" (click)="$event.stopPropagation()">
          @if (state.type === BulkModalType.Creation) {
            <motn-bulk-tile-creation-upload
              [deviceId]="state.data.deviceId"
              [points]="state.data.points"
              [protocol]="state.data.protocol"
              (close)="onCreationClose()"
              (proceedToProcessing)="onProceedToProcessing($event)"
            ></motn-bulk-tile-creation-upload>
          }

          @if (state.type === BulkModalType.Processing) {
            <motn-bulk-processing
              [processingItems]="state.data.processingItems"
              [deviceId]="state.data.deviceId"
              [isConfigurationMode]="state.data.isConfigurationMode || false"
              (close)="onProcessingClose()"
              (goBack)="onGoBack()"
            ></motn-bulk-processing>
          }

          @if (state.type === BulkModalType.Configuration) {
            <motn-bulk-tile-configuration-upload
              (close)="onConfigurationClose()"
              (proceedToProcessing)="onConfigurationProceedToProcessing($event)"
            ></motn-bulk-tile-configuration-upload>
          }
        </div>
      </div>
    }
  `,
  styles: [`
    .modal-backdrop {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: var(--motn-z-index-drawer);
      padding: 0 20px;
      overflow-y: auto;
      overflow-x: hidden;
    }

    .modal-container {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  `],
  imports: [
    BulkTileCreationUploadComponent,
    BulkProcessingComponent,
    BulkTileConfigurationUploadComponent
],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true
})
export class BulkModalComponent {
  BulkModalType = BulkModalType;

  // Single signal access - no subscriptions needed!
  modalState = this.bulkModalService.modalState;

  // Computed signals for specific data if needed
  isConfigurationMode = computed(() => {
    const state = this.modalState();
    return state.type === BulkModalType.Processing && state.data.isConfigurationMode;
  });

  constructor(private bulkModalService: BulkModalService) {}

  onCreationClose(): void {
    this.bulkModalService.close();
  }

  onProceedToProcessing(items: TileProcessingItem[]): void {
    const state = this.modalState();
    if (state.type !== BulkModalType.Creation) return;

    // Transition to processing modal
    this.bulkModalService.transitionToProcessing(items, state.data.deviceId!);
  }

  onProcessingClose(): void {
    this.bulkModalService.close();
  }

  onGoBack(): void {
    // Check if we're in configuration mode
    if (this.isConfigurationMode()) {
      this.bulkModalService.backToConfiguration();
    } else {
      this.bulkModalService.backToCreation();
    }
  }

  onConfigurationClose(): void {
    this.bulkModalService.close();
  }

  onConfigurationProceedToProcessing(items: TileProcessingItem[]): void {
    // Transition to processing modal in configuration mode
    this.bulkModalService.openProcessingModal({
      processingItems: items,
      isConfigurationMode: true
    });
  }

  onBackdropClick(event: Event): void {
    this.bulkModalService.close();
  }
}
